/**
 * Etiket basılacak ürünleri seçme paneli.
 *
 * Mevcut `InventoryProductSelectionModal` yeniden kullanılmadı çünkü (1) ürün
 * başına adet girişi gerekiyor, (2) o modalın alan eşlemesi `vatRate`,
 * `vatStatus` ve `saleCurrency`'yi taşımıyor — bunlar olmadan etikete yazılan
 * fiyat yanlış olur.
 *
 * Arama terimi backend'de ad + stok kodu + barkod alanlarında aranır, bu yüzden
 * tek kutu hem arama hem barkod okutma için yeterli.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { perfLog } from '../utils/labelPerf';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import trLocale from 'date-fns/locale/tr';
import { endOfDay, startOfDay } from 'date-fns';
import { toLocalIso } from '../../../utils/dateUtils';
import { accountingApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';
import { InventoryItem } from '../../accounting/inventory/types/inventory.types';
import {
  getVatIncludedPrice,
  formatMoney,
  formatDate,
  getCurrencySymbol,
} from '../dataSources/inventoryLabelSource';

export interface PickedRow {
  item: InventoryItem;
  quantity: number;
}

export interface InventoryPickerProps {
  rows: PickedRow[];
  onChange: (rows: PickedRow[]) => void;
  /** Yeni eklenen ürün için başlangıç adedi */
  defaultQuantity?: number;
}

/**
 * "Tüm sonuçları ekle" için üst sınır. Fiyat güncellemesi binlerce ürünü
 * kapsayabiliyor; tek istekte hepsini çekmek hem tarayıcıyı hem yazıcıyı boğar.
 */
const MAX_BULK_ADD = 500;

interface PagedResponse {
  items?: unknown[];
  Items?: unknown[];
  totalCount?: number;
  TotalCount?: number;
}

/**
 * Backend bazı ortamlarda PascalCase döndürdüğü için alanlar iki adla okunur
 * (projedeki mevcut savunmacı kalıp).
 */
function mapItem(raw: Record<string, unknown>): InventoryItem {
  const pick = <T,>(camel: string, pascal: string): T | undefined =>
    (raw[camel] as T) ?? (raw[pascal] as T);

  return {
    id: String(pick<string>('id', 'Id') ?? ''),
    name: String(pick<string>('name', 'Name') ?? ''),
    stockCode: String(pick<string>('stockCode', 'StockCode') ?? ''),
    barcode: pick<string>('barcode', 'Barcode') ?? undefined,
    // Etikette koşullu rozet ve menşei için gerekli
    isDomesticProduct: pick<boolean>('isDomesticProduct', 'IsDomesticProduct') === true,
    countryOfOrigin: pick<string>('countryOfOrigin', 'CountryOfOrigin') ?? null,
    lastPriceChangeDate: pick<string>('lastPriceChangeDate', 'LastPriceChangeDate') ?? null,
    categoryId: pick<string>('categoryId', 'CategoryId') ?? null,
    brandId: pick<string>('brandId', 'BrandId') ?? null,
    unit: String(pick<string>('unit', 'Unit') ?? 'Adet'),
    purchasePrice: Number(pick<number>('purchasePrice', 'PurchasePrice') ?? 0),
    salePrice: Number(pick<number>('salePrice', 'SalePrice') ?? 0),
    saleCurrency: pick<string>('saleCurrency', 'SaleCurrency') ?? 'TRY',
    vatRate: pick<number>('vatRate', 'VatRate') ?? undefined,
    vatStatus: pick<string>('vatStatus', 'VatStatus') ?? undefined,
    description: pick<string>('description', 'Description') ?? undefined,
    imageUrl: pick<string>('imageUrl', 'ImageUrl') ?? null,
    isActive: pick<boolean>('isActive', 'IsActive') ?? true,
    createdAt: String(pick<string>('createdAt', 'CreatedAt') ?? ''),
  } as InventoryItem;
}

/**
 * Basılacaklar listesindeki tek satır — bilinçli olarak düz HTML.
 *
 * Liste 100+ satıra çıkabiliyor. MUI TableRow/TableCell/TextField ile yazıldığında
 * her satır bir düzine emotion stil hesabı demek ve 100 ürünün listeye eklenmesi
 * ölçümde 2 saniye sürüyordu. Aynı görünümü düz `tr/td/input` ile inline stille
 * elde etmek bu maliyeti neredeyse tamamen kaldırıyor.
 */
const ROW_CELL: React.CSSProperties = {
  borderBottom: '1px solid rgba(224,224,224,1)',
  padding: '6px 12px',
  fontFamily: 'inherit',
};

const PickedRowItem: React.FC<{
  row: PickedRow;
  onQuantityChange: (id: string, quantity: number) => void;
  onRemove: (id: string) => void;
}> = React.memo(({ row, onQuantityChange, onRemove }) => (
  <tr>
    <td style={{ ...ROW_CELL, maxWidth: 300 }}>
      <div
        title={row.item.name}
        style={{
          fontSize: '0.875rem',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {row.item.name}
      </div>
      <div style={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)' }}>
        {row.item.stockCode}
        {!row.item.barcode && ' · barkod yok'}
      </div>
    </td>
    <td style={{ ...ROW_CELL, textAlign: 'center' }}>
      <input
        type="number"
        min={0}
        step={1}
        value={row.quantity}
        onChange={(e) => onQuantityChange(row.item.id, parseInt(e.target.value, 10) || 0)}
        style={{
          width: 60,
          textAlign: 'center',
          padding: '6px 4px',
          fontSize: '0.875rem',
          fontFamily: 'inherit',
          border: '1px solid rgba(0,0,0,0.23)',
          borderRadius: 4,
        }}
      />
    </td>
    <td style={{ ...ROW_CELL, textAlign: 'center' }}>
      <button
        type="button"
        onClick={() => onRemove(row.item.id)}
        title="Listeden çıkar"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: '#d32f2f',
          padding: 4,
          lineHeight: 0,
          borderRadius: '50%',
        }}
      >
        <DeleteOutlineIcon fontSize="small" />
      </button>
    </td>
  </tr>
));

const InventoryPicker: React.FC<InventoryPickerProps> = ({ rows, onChange, defaultQuantity = 1 }) => {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [priceFrom, setPriceFrom] = useState<Date | null>(null);
  const [priceTo, setPriceTo] = useState<Date | null>(null);
  const [checked, setChecked] = useState<Map<string, InventoryItem>>(new Map());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const searchRef = useRef<HTMLInputElement>(null);
  const requestId = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(search);
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  /**
   * Fiyat değişim logları veritabanına TR saatiyle yazılıyor, bu yüzden sınırlar
   * da yerel saat olarak gönderilir — `toISOString` UTC'ye çevirip 3 saat kaydırır.
   *
   * Gün başı/sonu genişletmesi şart: kullanıcı bitiş günü olarak bugünü
   * seçtiğinde gün içindeki değişiklikler de kapsanmalı, yoksa 00:00 sınırıyla
   * hiçbir sonuç dönmez.
   */
  const priceChangedFrom = useMemo(
    () => (priceFrom ? toLocalIso(startOfDay(priceFrom)) : undefined),
    [priceFrom],
  );
  const priceChangedTo = useMemo(
    () => (priceTo ? toLocalIso(endOfDay(priceTo)) : undefined),
    [priceTo],
  );
  const priceFilterActive = !!priceChangedFrom || !!priceChangedTo;

  /** Arama sonucu ve toplu ekleme aynı filtreyi kullanmalı */
  const buildFilterBody = useCallback(
    (pageNumber: number, pageSize: number) => ({
      pageNumber,
      pageSize,
      searchTerm: debounced || undefined,
      isActive: true,
      excludeVariantChildren: true,
      priceChangedFrom,
      priceChangedTo,
      // Etikete basılabilsin ve listede görünsün diye son fiyat değişim tarihi
      includeLastPriceChange: true,
    }),
    [debounced, priceChangedFrom, priceChangedTo],
  );

  // Filtre değişince ilk sayfaya dön; aksi halde boş sayfa görünür
  useEffect(() => {
    setPage(0);
  }, [priceChangedFrom, priceChangedTo]);

  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);

    accountingApi
      // backend 1-based, MUI 0-based
      .post<PagedResponse>(ACCOUNTING_API.INVENTORY_ITEMS_PAGED, buildFilterBody(page + 1, rowsPerPage))
      .then((res) => {
        // Yarışan istekler: yalnızca en son isteğin sonucu yazılır
        if (id !== requestId.current) return;
        const data = res.data ?? {};
        const list = (data.items ?? data.Items ?? []) as Record<string, unknown>[];
        setItems(list.map(mapItem));
        setTotal(Number(data.totalCount ?? data.TotalCount ?? 0));
      })
      .catch((err) => {
        if (id !== requestId.current) return;
        setError(err instanceof Error ? err.message : 'Ürünler yüklenemedi');
        setItems([]);
        setTotal(0);
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
  }, [buildFilterBody, page, rowsPerPage]);

  const selectedIds = useMemo(() => new Set(rows.map((r) => r.item.id)), [rows]);

  // Satır işlemlerinin güncel listeye erişmesi için — bkz. setQuantity/removeRow
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  /**
   * Ekleme süresi ölçümü. State güncellemesini ölçmek işe yaramıyor (anında
   * dönüyor); asıl maliyet sonraki render'da. Bu yüzden ekleme anında zaman
   * damgası bırakılıp aşağıdaki effect'te (commit sonrası) fark yazılıyor.
   */
  const pendingAdd = useRef<{ t0: number; label: string } | null>(null);
  const markPendingAdd = useCallback((label: string) => {
    pendingAdd.current = { t0: performance.now(), label };
  }, []);

  useEffect(() => {
    const p = pendingAdd.current;
    if (!p) return;
    pendingAdd.current = null;
    perfLog(`${p.label}: ${(performance.now() - p.t0).toFixed(1)} ms (render dahil)`);
  }, [rows]);

  const addItem = useCallback(
    (item: InventoryItem) => {
      const existing = rows.find((r) => r.item.id === item.id);
      markPendingAdd(`tek ürün ekleme (${rows.length} → ${rows.length + (existing ? 0 : 1)} satır)`);
      if (existing) {
        onChange(
          rows.map((r) =>
            r.item.id === item.id ? { ...r, quantity: r.quantity + defaultQuantity } : r,
          ),
        );
      } else {
        onChange([...rows, { item, quantity: defaultQuantity }]);
      }
    },
    [rows, onChange, defaultQuantity, markPendingAdd],
  );

  /**
   * Birden fazla ürünü listeye ekler. Zaten ekli olanlar atlanır — toplu
   * eklemede adet katlamak neredeyse hep istenmeyen sonuç veriyor.
   * Eklenen ürün sayısını döndürür.
   */
  const addItems = useCallback(
    (list: InventoryItem[]): number => {
      const existing = new Set(rows.map((r) => r.item.id));
      const fresh = list.filter((i) => i.id && !existing.has(i.id));
      if (fresh.length === 0) return 0;
      markPendingAdd(`toplu ekleme (${fresh.length} ürün → ${rows.length + fresh.length} satır)`);
      onChange([...rows, ...fresh.map((item) => ({ item, quantity: defaultQuantity }))]);
      return fresh.length;
    },
    [rows, onChange, defaultQuantity, markPendingAdd],
  );

  const toggleCheck = (item: InventoryItem) => {
    setChecked((prev) => {
      const next = new Map(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.set(item.id, item);
      return next;
    });
  };

  /** Başlıktaki kutu: sayfadaki tüm ürünleri işaretler veya bırakır */
  const togglePageCheck = () => {
    setChecked((prev) => {
      const next = new Map(prev);
      const allChecked = items.length > 0 && items.every((i) => next.has(i.id));
      if (allChecked) items.forEach((i) => next.delete(i.id));
      else items.forEach((i) => next.set(i.id, i));
      return next;
    });
  };

  const addChecked = () => {
    const added = addItems(Array.from(checked.values()));
    const skipped = checked.size - added;
    setChecked(new Map());
    setNote(
      skipped > 0
        ? `${added} ürün eklendi, ${skipped} tanesi listede zaten vardı.`
        : `${added} ürün eklendi.`,
    );
  };

  /**
   * Filtre sonucundaki tüm ürünleri ekler.
   * Görünen sayfa yerine filtrenin tamamı için ayrı bir istek atılır;
   * kullanıcı 500 sonucu sayfa sayfa gezmek zorunda kalmasın.
   */
  const addAllResults = async () => {
    setBulkLoading(true);
    setNote(null);
    setError(null);
    try {
      const size = Math.min(total, MAX_BULK_ADD);
      const res = await accountingApi.post<PagedResponse>(
        ACCOUNTING_API.INVENTORY_ITEMS_PAGED,
        buildFilterBody(1, size),
      );
      const data = res.data ?? {};
      const list = (data.items ?? data.Items ?? []) as Record<string, unknown>[];
      const added = addItems(list.map(mapItem));
      const skipped = list.length - added;

      const parts = [`${added} ürün eklendi.`];
      if (skipped > 0) parts.push(`${skipped} tanesi listede zaten vardı.`);
      if (total > MAX_BULK_ADD) {
        parts.push(`Sonuç ${total} ürün; ilk ${MAX_BULK_ADD} tanesi alındı.`);
      }
      setNote(parts.join(' '));
      setChecked(new Map());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ürünler eklenemedi');
    } finally {
      setBulkLoading(false);
    }
  };

  const clearPriceFilter = () => {
    setPriceFrom(null);
    setPriceTo(null);
  };

  /**
   * Satır işlemleri `rows`'a doğrudan bağlı değil, ref üzerinden okuyor.
   * Bağımlılık verilirse her ekleme sonrası yeni birer fonksiyon üretiliyor,
   * bu da 100 satırlık tabloda PickedRowItem memo'sunu işlevsiz bırakıp
   * tüm satırları yeniden çizdiriyor.
   */
  const setQuantity = useCallback(
    (id: string, quantity: number) => {
      onChange(
        rowsRef.current.map((r) =>
          r.item.id === id ? { ...r, quantity: Math.max(0, Math.floor(quantity)) } : r,
        ),
      );
    },
    [onChange],
  );

  const removeRow = useCallback(
    (id: string) => onChange(rowsRef.current.filter((r) => r.item.id !== id)),
    [onChange],
  );

  /** Barkod okuyucu Enter gönderir; tek sonuç varsa doğrudan ekle */
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (items.length === 0) return;

    const exact = items.find(
      (i) => i.barcode === search.trim() || i.stockCode === search.trim(),
    );
    addItem(exact ?? items[0]);
    setSearch('');
    searchRef.current?.focus();
  };

  const totalLabels = rows.reduce((sum, r) => sum + r.quantity, 0);

  return (
    <Stack spacing={1.5} sx={{ height: '100%' }}>
      <TextField
        inputRef={searchRef}
        size="small"
        fullWidth
        autoFocus
        placeholder="Ürün adı, stok kodu veya barkod ara — barkod okutup Enter'a basabilirsiniz"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={handleSearchKeyDown}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: loading ? <CircularProgress size={16} /> : undefined,
        }}
      />

      {/* Fiyat değişim tarihi aralığı — PriceChangeLogs tablosundan filtrelenir */}
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Tooltip title="Seçilen tarihler arasında fiyatı değişmiş ürünleri listeler">
            <Chip size="small" icon={<HistoryIcon fontSize="small" />} label="Fiyat değişimi" variant="outlined" />
          </Tooltip>
          <DatePicker
            label="Başlangıç"
            value={priceFrom}
            onChange={setPriceFrom}
            maxDate={priceTo ?? undefined}
            slotProps={{
              textField: { size: 'small', sx: { width: 155 } },
              field: { clearable: true, onClear: () => setPriceFrom(null) },
            }}
          />
          <DatePicker
            label="Bitiş"
            value={priceTo}
            onChange={setPriceTo}
            minDate={priceFrom ?? undefined}
            slotProps={{
              textField: { size: 'small', sx: { width: 155 } },
              field: { clearable: true, onClear: () => setPriceTo(null) },
            }}
          />
          {priceFilterActive && (
            <Button size="small" onClick={clearPriceFilter}>
              Filtreyi kaldır
            </Button>
          )}
        </Stack>
      </LocalizationProvider>

      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}

      {/* Toplu seçim çubuğu */}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary">
          {loading ? 'Aranıyor…' : `${total} ürün bulundu`}
          {priceFilterActive && ' (fiyat değişimine göre süzüldü)'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {checked.size > 0 && (
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={addChecked}>
            Seçilenleri ekle ({checked.size})
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={bulkLoading ? <CircularProgress size={14} /> : <PlaylistAddIcon />}
          onClick={addAllResults}
          disabled={bulkLoading || total === 0}
        >
          Tüm sonuçları ekle{total > 0 ? ` (${Math.min(total, MAX_BULK_ADD)})` : ''}
        </Button>
      </Stack>

      {note && (
        <Typography variant="caption" color="success.main">
          {note}
        </Typography>
      )}

      {/* Arama sonuçları */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'auto', maxHeight: 260 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  disabled={items.length === 0}
                  checked={items.length > 0 && items.every((i) => checked.has(i.id))}
                  indeterminate={items.some((i) => checked.has(i.id)) && !items.every((i) => checked.has(i.id))}
                  onChange={togglePageCheck}
                />
              </TableCell>
              <TableCell>Ürün</TableCell>
              <TableCell>Stok Kodu</TableCell>
              <TableCell>Barkod</TableCell>
              <TableCell align="right">Fiyat (KDV Dahil)</TableCell>
              <TableCell>Fiyat Değişimi</TableCell>
              <TableCell align="center" width={50} />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id} hover selected={selectedIds.has(item.id)}>
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={checked.has(item.id)} onChange={() => toggleCheck(item)} />
                </TableCell>
                <TableCell sx={{ maxWidth: 260 }}>
                  <Typography variant="body2" noWrap title={item.name}>
                    {item.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{item.stockCode}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color={item.barcode ? 'text.primary' : 'error'}>
                    {item.barcode || 'yok'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {formatMoney(getVatIncludedPrice(item))} {getCurrencySymbol(item.saleCurrency)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color={item.lastPriceChangeDate ? 'text.primary' : 'text.disabled'}>
                    {formatDate(item.lastPriceChangeDate) || 'kayıt yok'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Listeye ekle">
                    <IconButton size="small" onClick={() => addItem(item)}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="caption" color="text.secondary">
                    {priceFilterActive
                      ? 'Seçilen tarih aralığında fiyatı değişen ürün yok'
                      : 'Sonuç bulunamadı'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage="Satır"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        sx={{ '& .MuiTablePagination-toolbar': { minHeight: 40 } }}
      />

      {/* Seçilenler */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle2">Basılacak Etiketler</Typography>
        <Chip size="small" color="primary" label={`${rows.length} ürün · ${totalLabels} etiket`} />
      </Stack>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'auto', flex: 1, minHeight: 120 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Ürün</TableCell>
              <TableCell align="center" width={110}>
                Adet
              </TableCell>
              <TableCell align="center" width={50} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <PickedRowItem
                key={row.item.id}
                row={row}
                onQuantityChange={setQuantity}
                onRemove={removeRow}
              />
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="caption" color="text.secondary">
                    Yukarıdan ürün ekleyin
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Stack>
  );
};

export default InventoryPicker;
